import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import Alert from '@amzn/awsui-components-console/alert';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Spinner from '@amzn/awsui-components-console/spinner';
import Modal from '@amzn/awsui-components-console/modal';
import FormField from '@amzn/awsui-components-console/form-field';
import RadioGroup from '@amzn/awsui-components-console/radio-group';
import Textarea from '@amzn/awsui-components-console/textarea';
import Input from '@amzn/awsui-components-console/input';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import {
  DraftStringInput,
  InlineMarkupType,
  MessageFormatType,
} from 'src/features/string-group-detail/types';
import {
  persistFieldEdit,
  StringFieldEdit,
} from 'src/features/string-group-detail/utils/draftStorage';
import {
  CONTEXT_NOTE_CHAR_LIMIT,
  SOURCE_TEXT_CHAR_LIMIT,
} from 'src/features/string-group-detail/constants';
import {
  buildServerSnapshot,
  EditDraftContext,
  parseBaseVersion,
} from '../utils/editDraft';

interface SourceTextPanelProps {
  sourceLocale: string;
  sourceText: string;
  contextNote?: string | null;
  /** CX ID when in variant view */
  variantCxId?: string;
  /** Base string source text for comparison (variant view only) */
  baseSourceText?: string | null;
  /** Whether the base source text is still loading */
  baseSourceTextLoading?: boolean;
  /** String ID for the edit modal */
  stringId?: string;
  /**
   * Identity + base snapshot used to persist an edit as a draft change.
   *
   * Absent when the base (no-`cxId`) snapshot an edit must be written from is
   * unavailable — still in flight, or its request failed. Editing is then not
   * offered at all: there is deliberately no fallback to the variant-resolved
   * response, whose text is the variant's own and whose `variants` is null.
   */
  draftContext?: EditDraftContext;
  /**
   * This string's pending entry in the group draft, when it has one.
   *
   * The modal prefills from it in preference to the server response so a user
   * who already changed this string on the group page (or here, before) reopens
   * the modal on their own pending content rather than on the stale server text.
   * The page owns the read so there is one draft reader per view.
   */
  draftEntry?: DraftStringInput;
  /**
   * Notifies the page that the draft on disk changed, so it can re-read it and
   * refresh the "modified" badge and this component's `draftEntry`.
   *
   * Saving currently also redirects to the group page, so in practice the page
   * unmounts right after; the callback keeps the badge correct regardless of
   * that redirect rather than depending on it.
   */
  onDraftSaved?: () => void;
  /**
   * Whether this view may author an edit at all. Required (not defaulted) so a
   * caller cannot fall open by omission: the page decides from the source's
   * client ownership and whether a historical version is pinned.
   */
  canEdit: boolean;
  /**
   * True while the namespace permission is still in flight. Kept separate from
   * `canEdit` so the panel never renders "you may not edit" before the answer
   * arrives - the control is a skeleton until then, not a missing button.
   */
  permissionsLoading?: boolean;
}

/**
 * Field-level validation shared by the source text and context note.
 *
 * Mirrors the string-group page's EditStringModal rule-for-rule — both fields
 * required after trimming, same character limits, same messages — because both
 * modals feed the same draft changeset and must not drift apart.
 */
function fieldError(
  trimmedValue: string,
  label: string,
  charLimit: number,
): string | undefined {
  if (!trimmedValue) {
    return `${label} is required.`;
  }
  if (trimmedValue.length > charLimit) {
    return `${label} exceeds ${charLimit} character limit.`;
  }
  return undefined;
}

/**
 * RadioGroup option value standing for "the string has no format recorded".
 *
 * TRANSCOM reports an UNKNOWN format as an absent field, and the panel must be
 * able to render and re-select that state — a radio group cannot express it
 * otherwise, and preselecting PlainText/None would make an unrelated text edit
 * persist an invented format. Not a member of either enum, so it can never be
 * mistaken for a real choice.
 */
const UNKNOWN_FORMAT_VALUE = '__UNKNOWN__';

/**
 * Source-text panel with an edit modal.
 *
 * There is no per-string update mutation: source changes are submitted as a
 * whole string-group changeset. So saving here writes the edit into the same
 * localStorage draft the string-group detail page reads (via `useDraftState`)
 * and redirects there, where the user reviews the pending change and submits
 * the group for translation.
 *
 * That draft is the only copy of the edit, so the redirect is gated on a
 * CONFIRMED write: `persistFieldEdit` throws when localStorage rejects it
 * (quota exceeded, Safari private mode) and the modal then stays open with the
 * user's text intact.
 */
const SourceTextPanel: React.FC<SourceTextPanelProps> = ({
  sourceLocale,
  sourceText,
  contextNote,
  variantCxId,
  baseSourceText,
  baseSourceTextLoading,
  stringId,
  draftContext,
  draftEntry,
  onDraftSaved,
  canEdit,
  permissionsLoading = false,
}) => {
  const isVariant = !!variantCxId;
  const navigate = useNavigate();

  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editSourceText, setEditSourceText] = useState('');
  const [editContextNote, setEditContextNote] = useState('');
  // Undefined models "no format recorded for this string" — see
  // UNKNOWN_FORMAT_VALUE. It is a value the save path preserves, not a gap to
  // fill with a default.
  const [editInlineMarkupType, setEditInlineMarkupType] = useState<
    InlineMarkupType | undefined
  >(undefined);
  const [editMessageFormatType, setEditMessageFormatType] = useState<
    MessageFormatType | undefined
  >(undefined);
  // Whether the modal opened on an absent format. The "Unknown (keep as is)"
  // option is offered for exactly those fields and stays offered for the whole
  // session, so a mis-click on a real format can be undone without abandoning
  // the text edit. A string that already has a format is never offered a way to
  // unset it — TRANSCOM has no such transition.
  const [inlineMarkupUnknown, setInlineMarkupUnknown] = useState(false);
  const [messageFormatUnknown, setMessageFormatUnknown] = useState(false);
  /**
   * Why the last save attempt did not persist, when it did not.
   *
   * Set only from a failed strict write; while it is set the modal stays open
   * with its content, so the user can retry or copy the text out.
   */
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  /**
   * The pending variant this view edits, when the draft holds one for its CX ID.
   * Only meaningful in variant view — a base edit never reads it.
   */
  const pendingVariant =
    isVariant && draftEntry
      ? draftEntry.variants.find((v) => v.cxId === variantCxId)
      : undefined;

  // Validation is derived from the current values rather than computed on save,
  // so Save can be disabled while invalid. The errors render immediately (which
  // is why the disabled Save is never unexplained) — a pre-existing string with
  // no context note opens with the "required" hint already visible.
  const trimmedSourceText = editSourceText.trim();
  const trimmedContextNote = editContextNote.trim();
  const sourceTextError = fieldError(
    trimmedSourceText,
    'Source text',
    SOURCE_TEXT_CHAR_LIMIT,
  );
  const contextNoteError = fieldError(
    trimmedContextNote,
    'Context note',
    CONTEXT_NOTE_CHAR_LIMIT,
  );
  const isSaveDisabled = !!sourceTextError || !!contextNoteError;

  /**
   * Seeds the form. Precedence for every field is **pending draft value →
   * server response → default**, so reopening the modal continues the user's
   * own unsubmitted content instead of reverting it to what the server still
   * reports.
   *
   * The pending pair is taken whole (`entry ? … : …`) rather than field by field
   * with `??`: an entry whose note is absent means the draft has no note, not
   * "fall back to the server's note".
   */
  const handleEditClick = () => {
    if (isVariant) {
      setEditSourceText(
        pendingVariant ? pendingVariant.sourceText : sourceText,
      );
      setEditContextNote(
        pendingVariant
          ? (pendingVariant.contextNote ?? '')
          : (contextNote ?? ''),
      );
    } else {
      setEditSourceText(draftEntry ? draftEntry.sourceText : sourceText);
      setEditContextNote(
        draftEntry ? (draftEntry.contextNote ?? '') : (contextNote ?? ''),
      );
    }
    // Format is string-level, so the draft entry's value applies to both views.
    // Precedence is the same as the text fields', but the chain STOPS at absent:
    // a string with no recorded format seeds no selection (the "Unknown (keep as
    // is)" option) rather than PlainText/None, which a save would then persist.
    const seededInlineMarkup =
      draftEntry?.inlineMarkupType ??
      draftContext?.inlineMarkupType ??
      undefined;
    const seededMessageFormat =
      draftEntry?.messageFormatType ??
      draftContext?.messageFormatType ??
      undefined;
    setEditInlineMarkupType(seededInlineMarkup);
    setInlineMarkupUnknown(!seededInlineMarkup);
    setEditMessageFormatType(seededMessageFormat);
    setMessageFormatUnknown(!seededMessageFormat);
    // A previous attempt's failure must not greet the next open.
    setSaveError(undefined);
    setEditModalVisible(true);
  };

  const handleSave = (context: EditDraftContext) => {
    // A format the user left on "Unknown (keep as is)" is OMITTED from the
    // change, which `applyFieldEdit` reads as "leave the entry's value exactly
    // as it is" — including leaving it absent. Spelling out PlainText/None here
    // would silently give the string a format it never had.
    const formatEdit = {
      ...(editInlineMarkupType
        ? { inlineMarkupType: editInlineMarkupType }
        : {}),
      ...(editMessageFormatType
        ? { messageFormatType: editMessageFormatType }
        : {}),
    };

    // Only the field group this modal actually edited is expressed, so an
    // existing draft entry keeps its other pending values (base pair, sibling
    // variants) instead of being overwritten with server values. The format
    // choices ride along on both kinds because they are string-level in
    // TRANSCOM — a variant overrides source text, never the format — so a
    // variant edit's format choice updates the string's own fields. This is
    // exactly how the group page's EditStringModal models them.
    // The variant note is stored exactly as the base note is — the trimmed
    // string — matching what EditStringModal emits.
    const change: StringFieldEdit = isVariant
      ? {
          kind: 'variant',
          cxId: variantCxId,
          sourceText: trimmedSourceText,
          contextNote: trimmedContextNote,
          ...formatEdit,
        }
      : {
          kind: 'base',
          sourceText: trimmedSourceText,
          contextNote: trimmedContextNote,
          ...formatEdit,
        };

    // parseInt tolerates the API's stringified version; a non-numeric value
    // would poison the draft's optimistic-concurrency token.
    try {
      persistFieldEdit(
        context.namespaceId,
        context.stringGroupId,
        parseBaseVersion(context.stringGroupVersion),
        buildServerSnapshot(context),
        change,
      );
    } catch (err) {
      // The draft is the ONLY copy of this edit, so a rejected write must not
      // close the modal or navigate: doing so would discard the user's text
      // while reporting success. Everything typed stays on screen.
      setSaveError(
        err instanceof Error && err.message
          ? err.message
          : 'Unknown storage error.',
      );
      return;
    }

    setSaveError(undefined);
    setEditModalVisible(false);
    onDraftSaved?.();
    navigate(
      `/${encodeURIComponent(context.namespaceId)}/${encodeURIComponent(context.stringGroupId)}`,
    );
  };

  const handleCancel = () => {
    setSaveError(undefined);
    setEditModalVisible(false);
  };

  return (
    <>
      <Container
        header={
          <Header
            actions={
              permissionsLoading ? (
                <Skeleton display="inline-block" height="32px" width="70px" />
              ) : canEdit ? (
                <Button
                  variant="normal"
                  onClick={handleEditClick}
                  // Disabled until the base snapshot the draft is written from
                  // is available. In variant view that snapshot is a separate
                  // request: while it is in flight — or after it failed, when
                  // the page withholds `draftContext` entirely — editing would
                  // otherwise persist the variant's own text as the base text.
                  disabled={!draftContext || !!baseSourceTextLoading}
                  data-testid={
                    isVariant ? 'edit-variant-button' : 'edit-string-button'
                  }
                >
                  Edit
                </Button>
              ) : undefined
            }
          >
            Source Text
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/* Base source text comparison (variant view only) */}
          {isVariant && (
            <div>
              <Box variant="awsui-key-label" color="text-status-inactive">
                <em>{`Base source text (${sourceLocale})`}</em>
              </Box>
              {baseSourceTextLoading ? (
                <Box variant="p">
                  <Spinner size="normal" /> Loading base text…
                </Box>
              ) : (
                <Box
                  variant="p"
                  color="text-status-inactive"
                  data-testid="base-source-text"
                >
                  {baseSourceText ?? '—'}
                </Box>
              )}
            </div>
          )}

          {/* Divider between base and variant text */}
          {isVariant && <hr aria-hidden="true" />}

          <div>
            <Box
              variant="awsui-key-label"
              data-testid={
                isVariant ? 'variant-source-label' : 'source-locale-label'
              }
            >
              {isVariant
                ? `Variant source text (${sourceLocale}) — ${variantCxId}`
                : `Source text (${sourceLocale})`}
            </Box>
            <Box
              variant="p"
              data-testid={isVariant ? 'variant-source-text' : 'source-text'}
            >
              {sourceText}
            </Box>
          </div>
          {contextNote && (
            <div>
              <Box variant="awsui-key-label">Context note</Box>
              <Box
                variant="p"
                data-testid={
                  isVariant ? 'variant-context-note' : 'context-note'
                }
              >
                {contextNote}
              </Box>
            </div>
          )}
        </SpaceBetween>
      </Container>

      {editModalVisible && draftContext && (
        <Modal
          visible={editModalVisible}
          onDismiss={handleCancel}
          header={isVariant ? 'Edit variant' : 'Edit string'}
          footer={
            <Box float="right">
              <SpaceBetween size="xs" direction="horizontal">
                <Button
                  variant="link"
                  onClick={handleCancel}
                  data-testid={
                    isVariant
                      ? 'edit-variant-cancel-button'
                      : 'edit-cancel-button'
                  }
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleSave(draftContext)}
                  disabled={isSaveDisabled}
                  data-testid={
                    isVariant ? 'edit-variant-save-button' : 'edit-save-button'
                  }
                >
                  Save changes
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <div
            data-testid={isVariant ? 'edit-variant-modal' : 'edit-string-modal'}
          >
            <SpaceBetween size="m">
              {saveError && (
                <Alert
                  type="error"
                  header="Changes not saved"
                  data-testid="edit-save-error"
                >
                  {`Your changes could not be saved to this browser: ${saveError} Nothing has been discarded — copy your text elsewhere, free up browser storage, or try again.`}
                </Alert>
              )}
              <FormField label="String ID">
                <Input
                  value={stringId || ''}
                  disabled
                  data-testid={
                    isVariant
                      ? 'edit-variant-string-id'
                      : 'edit-string-id-input'
                  }
                />
              </FormField>
              {isVariant && (
                <FormField label="CX ID">
                  <Input
                    value={variantCxId || ''}
                    disabled
                    data-testid="edit-variant-cxid"
                  />
                </FormField>
              )}
              {!isVariant && (
                <FormField label="Source locale">
                  <Input
                    value={sourceLocale}
                    disabled
                    data-testid="edit-source-locale-select"
                  />
                </FormField>
              )}
              <FormField
                label="Source text *"
                errorText={sourceTextError}
                constraintText={`Character limit: ${SOURCE_TEXT_CHAR_LIMIT.toLocaleString()}`}
              >
                <Textarea
                  value={editSourceText}
                  onChange={({ detail }) => setEditSourceText(detail.value)}
                  rows={4}
                  data-testid={
                    isVariant ? 'edit-variant-source-text' : 'edit-source-text'
                  }
                />
              </FormField>
              <FormField
                label="Context note *"
                errorText={contextNoteError}
                constraintText={`Character limit: ${CONTEXT_NOTE_CHAR_LIMIT.toLocaleString()}`}
              >
                <Textarea
                  value={editContextNote}
                  onChange={({ detail }) => setEditContextNote(detail.value)}
                  rows={3}
                  data-testid={
                    isVariant
                      ? 'edit-variant-context-note'
                      : 'edit-context-note'
                  }
                />
              </FormField>
              {/* Inline markup / message format mirror the string-group page's
                  Edit String modal exactly — same control, labels and option
                  order — because both modals write the same draft entry. They
                  are string-level in TRANSCOM, so they are offered unchanged in
                  variant view and edit the string's fields, not the variant's.
                  The one addition is the leading "Unknown (keep as is)" option,
                  present only when the string has no recorded format: without it
                  the control would have to preselect a real value, and saving
                  would write that invented value over TRANSCOM's UNKNOWN. */}
              <FormField label="Inline Markup *">
                <RadioGroup
                  value={editInlineMarkupType ?? UNKNOWN_FORMAT_VALUE}
                  items={[
                    ...(inlineMarkupUnknown
                      ? [
                          {
                            value: UNKNOWN_FORMAT_VALUE,
                            label: 'Unknown (keep as is)',
                          },
                        ]
                      : []),
                    { value: InlineMarkupType.PlainText, label: 'Plain Text' },
                    { value: InlineMarkupType.Html, label: 'HTML' },
                    { value: InlineMarkupType.Markdown, label: 'Markdown' },
                  ]}
                  onChange={({ detail }) =>
                    setEditInlineMarkupType(
                      detail.value === UNKNOWN_FORMAT_VALUE
                        ? undefined
                        : (detail.value as InlineMarkupType),
                    )
                  }
                  data-testid={
                    isVariant
                      ? 'edit-variant-inline-markup'
                      : 'edit-inline-markup'
                  }
                />
              </FormField>

              <FormField label="Message Format *">
                <RadioGroup
                  value={editMessageFormatType ?? UNKNOWN_FORMAT_VALUE}
                  items={[
                    ...(messageFormatUnknown
                      ? [
                          {
                            value: UNKNOWN_FORMAT_VALUE,
                            label: 'Unknown (keep as is)',
                          },
                        ]
                      : []),
                    { value: MessageFormatType.None, label: 'None' },
                    { value: MessageFormatType.HashGet, label: 'Hash-Get' },
                    {
                      value: MessageFormatType.AmazonMessageFormat,
                      label: 'Amazon Message Format',
                    },
                    { value: MessageFormatType.Mustache, label: 'Mustache' },
                  ]}
                  onChange={({ detail }) =>
                    setEditMessageFormatType(
                      detail.value === UNKNOWN_FORMAT_VALUE
                        ? undefined
                        : (detail.value as MessageFormatType),
                    )
                  }
                  data-testid={
                    isVariant
                      ? 'edit-variant-message-format'
                      : 'edit-message-format'
                  }
                />
              </FormField>
            </SpaceBetween>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SourceTextPanel;
